import { useEffect, useState } from 'react';
import { Mail, X } from 'lucide-react';
import {
  onAuthStateChanged,
  sendEmailVerification,
  type User,
} from 'firebase/auth';

import { auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';

/**
 * Soft email-verification banner.
 *
 * Shown above the dashboard content when the signed-in Firebase user has
 * not yet verified their email. Google/OAuth signups are auto-verified by
 * the provider, so this only appears for the email/password path.
 *
 * The banner reloads the Firebase user on window focus so it disappears
 * automatically once the user clicks the verification link in another tab
 * and comes back.
 */
export function EmailVerificationBanner() {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState<Date | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, []);

  useEffect(() => {
    function refreshUser() {
      auth.currentUser
        ?.reload()
        .then(() => setUser(auth.currentUser))
        .catch(() => {
          // Reload can fail if the token expired; ignore — the next auth
          // state change will resync.
        });
    }
    window.addEventListener('focus', refreshUser);
    return () => window.removeEventListener('focus', refreshUser);
  }, []);

  if (!user || user.emailVerified || dismissed) return null;

  async function handleResend() {
    if (!auth.currentUser) return;
    setResending(true);
    try {
      await sendEmailVerification(auth.currentUser, {
        url: 'https://app.heymotek.com/',
      });
      setResentAt(new Date());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Resend verification email failed:', err);
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-card px-4 py-2 text-sm dark:border-amber-900/40">
      <Mail className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="flex-1">
        {resentAt ? (
          <>
            Verification email sent again at{' '}
            {resentAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
          </>
        ) : (
          <>
            Verify your email. We sent a link to <strong>{user.email}</strong>.
          </>
        )}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleResend}
        disabled={resending}
      >
        {resending ? 'sending...' : 'Resend'}
      </Button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
